import { useEffect } from 'react';
import WalletAlpha from '../../services/walletAlpha/walletAlpha';
import { useOnMessage, useRequestMessage } from '../../utils/hooks';
import useStyles from './styles';
import useStoreWhereLoggedIn from './useStoreWhereLoggedIn';
import useAuthToken from './useAuthToken';
import useAuthUser from './useAuthUser';
import useApi from '../../services/api/useApi';
import { camelToSnake } from '../../utils/common/common';
import isSecurityCheckoutVerify from '../../utils/isSecurityCheckoutDomain/isSecurityCheckoutDomain';
import useLogger from '../../services/logger/useLogger';
import AuthUtils from '../../utils/auth.utils';

function AuthData() {
  const { getOrRefreshAccessToken, clearTokens, renewTokens } = useAuthToken();
  const { remove: removeUser, getUser, storeUser, loginUser } = useAuthUser();
  const classes = useStyles();
  const api = useApi();
  const { readStore } = useStoreWhereLoggedIn();
  const { info } = useLogger();

  const removeAuthenticatedData = () => {
    clearTokens();
    removeUser();
  };

  const handleLoggedUserError = (reply: ReplyCallback, cause: string) => {
    reply({
      customMessage: WalletAlpha.messages.NUVEM_CLEAR_AUTH_DATA,
      data: { cause, wasCrossStorePossible: AuthUtils.isCrossStorePossible() },
    });
  };

  useOnMessage(
    WalletAlpha.messages.NUVEM_LOGIN,
    async (messageData: WalletAlphaMessageData<LoginRequestBody>, reply, replyError) => {
      try {
        const response = await api.login(messageData.data);
        await loginUser({ ...response, storeId: messageData.data.storeId });

        reply({
          data: {
            user: camelToSnake(response.user),
            refresh_token_expiration_date: response.refreshTokenExpirationDate,
          },
        });
      } catch (error) {
        replyError({ error });
      }
    }
  );

  useOnMessage(
    WalletAlpha.messages.NUVEM_SESSION_MIGRATION,
    async (
      { data: { storeId, currentSession } }: WalletAlphaMessageData<SessionMigrationData>,
      reply
    ) => {
      const user = await loginUser({ ...currentSession, storeId });

      await info('Session migration V3 completed');

      reply({
        data: { user: camelToSnake(user), storeId },
        customMessage: WalletAlpha.messages.NUVEM_LOGGED_USER,
      });
    }
  );

  useOnMessage(
    WalletAlpha.messages.NUVEM_LOGGED_USER,
    async (messageData: WalletAlphaMessageData<LoggedUserData>, reply, replyError, origin) => {
      getOrRefreshAccessToken()
        .then(({ accessToken, expires }) => {
          const storedUser = getUser();

          const storefrontEmail = messageData.data?.email;

          const emailsMatch = !storefrontEmail || storedUser?.email === storefrontEmail;

          const authenticatedInAlpha = messageData.data?.authenticated;

          if (!accessToken || !storedUser || !emailsMatch) {
            if (!authenticatedInAlpha) {
              // If the reason we're logging out the user is that our email doesn't match with the one on the storefront,
              // then the user is not logged in => Alpha won't have an access token => it won't send us NUVEM_LOGOUT.
              removeAuthenticatedData();
            }

            handleLoggedUserError(
              reply,
              accessToken
                ? `NO_USER_FOUND ${JSON.stringify({
                    storedUser: storedUser?.email,
                    storefrontEmail,
                  })}`
                : 'NEW_CART'
            );
            return;
          }

          if (origin) {
            const isSecurityCheckout = isSecurityCheckoutVerify(origin);

            if (isSecurityCheckout) {
              reply({
                data: {
                  accessToken,
                },
              });
              return;
            }
          }

          const userEmail: string = storefrontEmail ?? storedUser.email;

          api
            .getUser(userEmail)
            .then((userData) => {
              storeUser({ email: userData.user.email }, expires?.toISOString() as string);

              reply({
                data: {
                  user: camelToSnake(userData.user),
                  storeId: readStore(),
                  sessionExpirationDate: expires?.toISOString(),
                },
              });
            })
            .catch((error) => {
              // If this request failed (ex: the user was deleted) don't remove data yet since that will occur after
              // receiving the NUVEM_LOGOUT event.
              handleLoggedUserError(reply, `Error getting user: ${error.message}`);
            });
        })
        .catch((error) => {
          removeAuthenticatedData();
          handleLoggedUserError(reply, `Error refreshing token: ${error.message}`);
        });
    }
  );

  useRequestMessage(WalletAlpha.messages.NUVEM_CREATE_ORDER, async (data: CreateOrderRequestBody) =>
    api.createOrder(camelToSnake(data) as CreateOrderRequestBody)
  );

  useRequestMessage(WalletAlpha.messages.NUVEM_UPDATE_USER, async (data: UpdateUserRequestBody) =>
    api.updateUser(data)
  );

  useRequestMessage(
    WalletAlpha.messages.NUVEM_SHOW_ONE_TAP_SURVEY,
    async (data: ShowSurveyRequestData) => api.shouldShowOneTapSurvey(data)
  );

  useRequestMessage(WalletAlpha.messages.NUVEM_UPDATE_ADDRESS, async (data: Address) =>
    api.updateAddressForUser(data)
  );

  useRequestMessage(WalletAlpha.messages.NUVEM_SEND_CES, async (data: CESRequestBody) =>
    api.sendCES(data)
  );

  useRequestMessage(WalletAlpha.messages.NUVEM_FETCH_ADDRESS, async (data: Address) =>
    api.getUserAddresses(data)
  );

  useOnMessage(WalletAlpha.messages.NUVEM_LOGOUT, async ({ data }, reply, replyError) => {
    getOrRefreshAccessToken()
      .then((token) => {
        if (!token.accessToken) {
          replyError({ error: new Error('No access token found') });
          return;
        }

        api
          .logout(data)
          .then(() => {
            removeAuthenticatedData();
            reply({ data: { success: true } });
          })
          .catch((error) => replyError({ error }));
      })
      .catch((error) => replyError({ error }));
  });

  useOnMessage(WalletAlpha.messages.NUVEM_SET_AUTHORIZATION, async (data, reply, replyError) => {
    getOrRefreshAccessToken()
      .then(({ accessToken }) => {
        if (!accessToken) {
          replyError({ error: new Error('No token found') });
          return;
        }

        api.addAuthorizationHeader(accessToken);
      })
      .catch((error) => replyError({ error }));
  });

  useOnMessage(WalletAlpha.messages.NUVEM_REFRESH_TOKEN, async (data, reply, replyError) => {
    renewTokens()
      .then((renewedTokens) => {
        if (renewedTokens) {
          api.addAuthorizationHeader(renewedTokens.accessToken);
        }

        reply();
      })
      .catch((error) => {
        replyError({ error });
      });
  });

  useEffect(() => {
    WalletAlpha.postMessage(
      WalletAlpha.messages.NUVEM_AUTH_DATA_READY,
      {
        data: 'AUTH_DATA_READY',
        isCrossStoreImpossible: !AuthUtils.isCrossStorePossible(),
      },
      '*'
    );
  }, []);

  return (
    <div className={classes.container} data-testid="authDataContainer">
      <p>AuthData has loaded</p>
    </div>
  );
}

export default AuthData;
